import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../lib/auth/hooks';
import { AuthLayout } from './AuthLayout';
import { AuthPage } from './AuthPage';
import { DashboardPage } from './DashboardPage';
import { DevDashboardPage } from './DevDashboardPage';
import { RootLayout } from './RootLayout';

// Placeholder page component
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div style={{ padding: 32 }}>
      <h1>{title}</h1>
      <p>Coming soon...</p>
    </div>
  );
}

/**
 * AuthGuard — redirects to /auth if user is not authenticated.
 * AuthPage handles all states internally (identifier, authenticate, etc.)
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          fontFamily: 'var(--font-family-primary)',
          color: 'var(--color-label-tertiary)',
        }}
      >
        Loading...
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        {/* Unified auth — handles identifier, authenticate, forgot, magic-link, QR */}
        <Route path="/auth" element={<AuthPage />} />

        {/* Backwards compatibility redirects */}
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/onboarding" element={<Navigate to="/auth" replace />} />

        {/* Authenticated routes — requires session */}
        <Route element={<AuthLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="animals" element={<PlaceholderPage title="Animals" />} />
          <Route path="animals/:id" element={<PlaceholderPage title="Animal Detail" />} />
          <Route path="journal" element={<PlaceholderPage title="Journal" />} />
          <Route path="welfare" element={<PlaceholderPage title="Welfare" />} />
          <Route path="nutrition" element={<PlaceholderPage title="Nutrition" />} />
          <Route path="management" element={<PlaceholderPage title="Management" />} />
          <Route path="projects" element={<PlaceholderPage title="Projects" />} />
          <Route path="sections" element={<PlaceholderPage title="Sections" />} />
          <Route path="assets" element={<PlaceholderPage title="Assets" />} />
          <Route path="analytics" element={<PlaceholderPage title="Analytics" />} />
          <Route path="notifications" element={<PlaceholderPage title="Notifications" />} />
          <Route path="settings" element={<PlaceholderPage title="Settings" />} />
          <Route path="chat" element={<PlaceholderPage title="Chat" />} />
          <Route path="dms" element={<PlaceholderPage title="Documents" />} />
          <Route path="users" element={<PlaceholderPage title="Users" />} />
        </Route>

        {/* Dev dashboard — development only */}
        {import.meta.env.DEV && <Route path="dev" element={<DevDashboardPage />} />}

        {/* Catch-all — redirect based on auth state */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Route>
    </Routes>
  );
}

/**
 * Catch-all redirect: sends user to the appropriate page based on auth state.
 * AuthPage handles onboarding vs. login internally based on stored identities.
 */
function CatchAllRedirect() {
  const { session, loading } = useAuth();

  if (loading) return null;

  if (!session) return <Navigate to="/auth" replace />;
  return <Navigate to="/" replace />;
}
